import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Button, CircularProgress, Link, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Grid, Divider
} from '@mui/material';

const PLAN_OPTIONS = [
    "E3 I4",
    "E2 I4",
    "E1 I4",
    "E3 I2",
    "E2 I2",
    "E1 I2",
    "E3 I1",
    "E2 I1",
    "E1 I1",
];

const WEEK_DAYS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

function getPackCount(pack) {
  if (!pack) return 0;
  if (pack.startsWith('E1')) return 1;
  if (pack.startsWith('E2')) return 2;
  if (pack.startsWith('E3')) return 3;
  return 0;
}
const ExpirySubscriptionList = () => {
  const [data, setData] = useState([]);
  const [firstStartDate, setFirstStartDate] = useState('');
  const [lastExpireDate, setLastExpireDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Renew dialog state
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewSub, setRenewSub] = useState(null);
  const [renewDate, setRenewDate] = useState('');
  const [renewMonths, setRenewMonths] = useState(1);
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewSchedule, setRenewSchedule] = useState([]);
  const [renewPlan, setRenewPlan] = useState('');
  const [schedule, setSchedule] = useState([{ day: '', time: '' }]);
  const [renewPrice, setRenewPrice] = useState(0);
  const [renewTransactionId, setRenewTransactionId] = useState('');

  // Update schedule array size and prefill values when renewPlan or renewSub changes
  useEffect(() => {
    const count = getPackCount(renewPlan);
    let defaultSchedule = [];
    let defaultPrice = 0;
    // Try to prefill from last subscription's schedule and price if available
    if (renewSub && renewSub.subscriptions && renewSub.subscriptions[0]) {
      const sub = renewSub.subscriptions[0];
      if (Array.isArray(sub.schedule)) {
        defaultSchedule = sub.schedule.map(s => ({
          day: s.day || '',
          time: s.time || ''
        }));
      }
      defaultPrice = sub.price || 0;
    }
    // Ensure array has correct length and prefill as much as possible
    while (defaultSchedule.length < count) defaultSchedule.push({ day: '', time: '' });
    while (defaultSchedule.length > count) defaultSchedule.pop();
    setSchedule(defaultSchedule);
    setRenewPrice(defaultPrice);
  }, [renewPlan, renewSub]);

  useEffect(() => {
    setLoading(true);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/list?page=${page + 1}&limit=${rowsPerPage}&&status=${encodeURIComponent(['cancelled'].join(','))}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        setData(result.data || []);
        setTotal(result.total || 0);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching expiry list:', err);
        setData([]);
        setLoading(false);
      });
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Open renew dialog
  const handleRenewOpen = (item) => {
    const sub = (item.subscriptions && item.subscriptions[0]) || {};
    let nextDay = '';
    if (sub.subscriptionExpireDate) {
      const d = new Date(sub.subscriptionExpireDate);
      d.setDate(d.getDate() + 1);
      nextDay = d.toISOString().slice(0, 10);
    }
    setRenewSub(item);
    setRenewDate(nextDay);
    setRenewMonths(1);
    setRenewPlan(sub.subscriptionPlan || '');
    setSchedule(sub.schedule || [{ day: '', time: '' }]);
    setRenewOpen(true);
  };

  const handleScheduleChange = (idx, field, value) => {
    setSchedule(prev =>
      prev.map((s, i) => i === idx ? { ...s, [field]: value } : s)
    );
  };


  // Renew subscription API call
  const handleRenewSubmit = async () => {
    if (!renewSub || !renewDate || !renewMonths) return;
    setRenewLoading(true);
    const sub = (renewSub.subscriptions && renewSub.subscriptions[0]) || {};
    const car = renewSub.carInfo || {};
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;

    // Calculate expire date
    const startDateObj = new Date(renewDate);
    const expireDateObj = new Date(renewDate);
    expireDateObj.setMonth(expireDateObj.getMonth() + Number(renewMonths));
    const payload = {
      subscriptionPlan: renewPlan,
      subscriptionStartDate: startDateObj,
      subscriptionExpireDate: expireDateObj,  
      washer: sub.washerId,
      car: sub.carId,
      schedule: schedule,
      price: renewPrice,
      transactionId: renewTransactionId,
    };

    try {
      await fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptions/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(payload),
      });
      setRenewLoading(false);
      setRenewOpen(false);
      alert('Subscription renewed!');
    } catch {
      setRenewLoading(false);
      alert('Failed to renew subscription.');
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Cancelled Subscriptions</Typography>

  <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer Name</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Scheduled</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>Expire Date</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Renewed on</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map(sub => (
              <TableRow key={sub._id}>
                <TableCell>
                  <Button
                    variant="text"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    // onClick={() => handleCustomerNameClick(sub)}
                  >
                    {(sub.userId?.name || '-') + ' (' + (sub.carId?.regNo || '-') + ')'}
                  </Button>
                </TableCell>
                <TableCell>
                <Button
                    variant="text"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    // onClick={() => navigate(`/subscription/${sub._id}`)}
                  >
                    {sub.currentSubscriptionPlan || '-'}
                  </Button>
                </TableCell>
                <TableCell>
                    {Array.isArray(sub.schedule) ? sub.schedule.length > 0
                      ? sub.currentSchedule.map(s =>
                          typeof s === 'object' && s.day
                            ? s.day
                            : typeof s === 'string'
                              ? s
                              : '-'
                        ).join(', ')
                      : '-': sub.currentSchedule?.exterior.map(s =>s.day).join(', ')}
                </TableCell>
                <TableCell>
                  {sub.startDate
                    ? new Date(sub.startDate).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell>
                  {sub.expireDate
                    ? new Date(sub.expireDate).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell>{sub.currentSubscriptionPlanPrice || 0}</TableCell>
                <TableCell>{new Date(sub.lastRenewalDate).toLocaleDateString() || '-'}</TableCell>
                <TableCell>{sub.status || '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    {/* <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleAddGraceDays(sub._id)}
                    >
                      Add grace days
                    </Button>
                    Comming soon.... */}
                    {/* <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleDelete(sub._id)}
                    >
                      Delete
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleStop(sub._id)}
                    >
                      Stop
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handlePauseOpen(sub._id)}
                    >
                      Pause
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleSkip(sub._id)}
                    >
                      Skip
                    </Button> */}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      <Dialog open={renewOpen} onClose={() => setRenewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Renew Subscription</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Subscription Plan"
                value={renewPlan}
                onChange={e => setRenewPlan(e.target.value)}
                fullWidth
                margin="dense"
                required
              >
                {PLAN_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Start Date"
                type="date"
                value={renewDate}
                onChange={e => setRenewDate(e.target.value)}
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="No. of Months"
                type="number"
                value={renewMonths}
                onChange={e => setRenewMonths(e.target.value)}
                fullWidth
                margin="dense"
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Subscription Price"
                type="number"
                value={renewPrice}
                onChange={e => setRenewPrice(Number(e.target.value))}
                fullWidth
                margin="dense"
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Transaction ID"
                value={renewTransactionId}
                onChange={e => setRenewTransactionId(e.target.value)}
                fullWidth
                margin="dense"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Schedule</Typography>
                {getPackCount(renewPlan) > 0 && (
                  <Grid container spacing={1}>
                    {[...Array(getPackCount(renewPlan))].map((_, idx) => (
                      <React.Fragment key={idx}>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            select
                            label={`Day ${idx + 1}`}
                            value={schedule[idx]?.day || ''}
                            onChange={e => handleScheduleChange(idx, 'day', e.target.value)}
                            fullWidth
                            margin="dense"
                            size="small"
                          >
                            {WEEK_DAYS.map(day => (
                              <MenuItem key={day} value={day}>{day}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                          <TextField
                            label={`Time ${idx + 1}`}
                            type="time"
                            value={schedule[idx]?.time || ''}
                            onChange={e => handleScheduleChange(idx, 'time', e.target.value)}
                            fullWidth
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRenewOpen(false)} disabled={renewLoading}>Cancel</Button>
          <Button onClick={handleRenewSubmit} variant="contained" color="primary" disabled={renewLoading}>
            {renewLoading ? 'Renewing...' : 'Renew'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpirySubscriptionList;
